<template>
  <div class="timestamp-formatter">
    <div class="section">
      <h2 class="section-title">当前时间戳</h2>
      <p class="hint">点击按钮刷新为最新时间</p>
      <div class="row">
        <div class="field">
          <label>秒级（Unix 秒）</label>
          <div class="field-inline">
            <input type="text" readonly :value="currentSeconds" class="mono-input" />
            <button type="button" class="btn" @click="copyText(String(currentSeconds))">复制</button>
          </div>
        </div>
        <div class="field">
          <label>毫秒级</label>
          <div class="field-inline">
            <input type="text" readonly :value="currentMillis" class="mono-input" />
            <button type="button" class="btn" @click="copyText(String(currentMillis))">复制</button>
          </div>
        </div>
      </div>
      <div class="field">
        <label>本地格式化</label>
        <div class="field-inline">
          <input type="text" readonly :value="currentFormatted" class="mono-input wide" />
          <button type="button" class="btn" @click="copyText(currentFormatted)">复制</button>
        </div>
      </div>
      <button type="button" class="btn primary" @click="refreshNow">刷新当前时间</button>
    </div>

    <div class="section">
      <h2 class="section-title">时间戳 → 格式化字符串</h2>
      <p class="hint">支持秒级（如 1714147262）或毫秒级（如 1714147262123），输出格式：YYYY-MM-DD HH:mm:ss（本地时区）</p>
      <div class="field">
        <label for="ts-input">输入数字时间戳</label>
        <input
          id="ts-input"
          v-model.trim="timestampInput"
          type="text"
          class="mono-input full"
          placeholder="仅输入数字，可含空格"
          @input="onConvertInput"
        />
      </div>
      <div class="radio-row">
        <label><input v-model="unitMode" type="radio" value="auto" @change="onConvertInput" /> 自动识别（≥1e12 视为毫秒）</label>
        <label><input v-model="unitMode" type="radio" value="s" @change="onConvertInput" /> 按秒解析</label>
        <label><input v-model="unitMode" type="radio" value="ms" @change="onConvertInput" /> 按毫秒解析</label>
      </div>
      <p v-if="convertError" class="error">{{ convertError }}</p>
      <div v-else class="field">
        <label>格式化结果</label>
        <div class="field-inline">
          <input type="text" readonly :value="convertedFormatted" class="mono-input wide" />
          <button type="button" class="btn" :disabled="!convertedFormatted" @click="copyText(convertedFormatted)">复制</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'

/**
 * 时间戳工具：展示当前秒/毫秒时间戳与本地格式化时间，并将用户输入的秒或毫秒时间戳转为 YYYY-MM-DD HH:mm:ss。
 */
export default {
  name: 'TimestampFormatterView',
  data() {
    return {
      /** @type {number} 当前毫秒时间戳 */
      currentMillis: Date.now(),
      /** 用户输入的原始字符串 */
      timestampInput: '',
      /** 解析单位：auto | s | ms */
      unitMode: 'auto',
      /** 转换错误提示 */
      convertError: '',
      /** 转换后的格式化字符串 */
      convertedFormatted: ''
    }
  },
  computed: {
    /** 当前秒级时间戳 */
    currentSeconds() {
      return Math.floor(this.currentMillis / 1000)
    },
    /** 当前时间的本地格式化字符串 */
    currentFormatted() {
      return this.formatDateTime(new Date(this.currentMillis))
    }
  },
  mounted() {
    this.refreshNow()
  },
  methods: {
    /**
     * 将 Date 格式化为 YYYY-MM-DD HH:mm:ss（本地时区）
     * @param {Date} d
     * @returns {string}
     */
    formatDateTime(d) {
      const pad = (n) => String(n).padStart(2, '0')
      const y = d.getFullYear()
      const m = pad(d.getMonth() + 1)
      const day = pad(d.getDate())
      const h = pad(d.getHours())
      const min = pad(d.getMinutes())
      const s = pad(d.getSeconds())
      return `${y}-${m}-${day} ${h}:${min}:${s}`
    },
    /** 刷新「当前时间」区块 */
    refreshNow() {
      this.currentMillis = Date.now()
    },
    /** 输入变化时尝试转换 */
    onConvertInput() {
      const raw = this.timestampInput.replace(/\s+/g, '')
      this.convertError = ''
      this.convertedFormatted = ''
      if (!raw) return
      if (!/^\d+$/.test(raw)) {
        this.convertError = '请输入纯数字时间戳'
        return
      }
      const n = Number(raw)
      if (!Number.isFinite(n) || n < 0) {
        this.convertError = '无效的数字'
        return
      }
      let ms
      if (this.unitMode === 's') {
        ms = n * 1000
      } else if (this.unitMode === 'ms') {
        ms = n
      } else {
        // 自动：通常毫秒 ≥ 1e12，秒约 1e9～1e10
        ms = n >= 1e12 ? n : n * 1000
      }
      const d = new Date(ms)
      if (Number.isNaN(d.getTime())) {
        this.convertError = '无法解析为合法日期'
        return
      }
      this.convertedFormatted = this.formatDateTime(d)
    },
    /**
     * 复制文本到剪贴板
     * @param {string} text
     */
    async copyText(text) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success('已复制')
      } catch (e) {
        ElMessage.error('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.timestamp-formatter {
  max-width: 720px;
  margin: 20px auto;
  padding: 24px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section {
  margin-bottom: 32px;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  color: #2c3e50;
}

.hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: #7f8c8d;
  line-height: 1.5;
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.field {
  margin-bottom: 12px;
  flex: 1;
  min-width: 200px;
}

.field label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #34495e;
  font-weight: 500;
}

.field-inline {
  display: flex;
  gap: 8px;
  align-items: center;
}

.mono-input {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  font-family: ui-monospace, Consolas, monospace;
  font-size: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #f8f9fa;
}

.mono-input.wide {
  min-width: 220px;
}

.mono-input.full {
  width: 100%;
  box-sizing: border-box;
}

.btn {
  padding: 8px 14px;
  border: 1px solid #bdc3c7;
  border-radius: 6px;
  background: #ecf0f1;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: #dfe6e9;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.primary {
  background: #3498db;
  border-color: #2980b9;
  color: #fff;
}

.btn.primary:hover {
  background: #2980b9;
}

.radio-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 12px 0;
  font-size: 14px;
  color: #34495e;
}

.radio-row label {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.error {
  color: #e74c3c;
  font-size: 14px;
  margin: 8px 0;
}
</style>
